import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Deal, User } from '../../types'
import DealWorkspace from './DealWorkspace'

/**
 * Opens a single deal's workspace from a `/deals/:id` URL.
 *
 * Before routing existed there was no way to point a colleague at a deal — you
 * could only tell them which screen to open and what to search for.
 */
export default function DealDeepLink({
  user,
  dealId,
  onClose,
}: {
  user: User
  dealId: string
  onClose: () => void
}) {
  const [deal, setDeal] = useState<Deal | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      const { data, error: err } = await supabase
        .from('deals')
        .select('*')
        .eq('id', dealId)
        .maybeSingle()
      if (cancelled) return
      if (err) setError(err.message)
      else if (!data) setError('That deal could not be found. It may have been archived, or the link may be wrong.')
      else setDeal(data as Deal)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [dealId, reloadKey])

  if (loading) {
    return (
      <div className="workspace-overlay" onClick={onClose}>
        <div className="workspace-content" onClick={(e) => e.stopPropagation()}>
          <div className="workspace-body">
            <p style={{ padding: 32, color: 'var(--txt2)' }}>Opening deal…</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !deal) {
    return (
      <div className="workspace-overlay" onClick={onClose}>
        <div className="workspace-content" onClick={(e) => e.stopPropagation()}>
          <div className="workspace-header">
            <h3>Deal not available</h3>
            <button className="workspace-close" onClick={onClose}>✕</button>
          </div>
          <div className="workspace-body">
            <p style={{ padding: '8px 0 20px', color: 'var(--txt2)' }}>{error}</p>
            <button className="ws-advance-btn" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <DealWorkspace
      user={user}
      deal={deal}
      onClose={onClose}
      onChanged={() => setReloadKey((k) => k + 1)}
    />
  )
}
